/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useEffect, useState, useCallback } from "@wordpress/element";

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import { fetchData } from "../api";
import { Spinner } from "@wordpress/components";
import Pagination from "./Pagination.component";
import BSHelper from "../BSHelper";
import { Fancybox } from "@fancyapps/ui";
import { getLibraryViewer } from "./report-viewer-url";

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Reports({
	year = null,
	downloadBtn = 0,
}) {
	const [libraries, setLibraries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const itemsPerPage = 5;
	const flippingBookViewerEnabled =
		window.bseitEditorVars?.flippingBookPilotEnabled === true ||
		window.bseitEditorVars?.flippingBookPilotEnabled === "1";
	const arrowClassIcon =
		" transition-all " +
		(BSHelper.isPageRTL() ? "fa-arrow-left-long" : "fa-arrow-right-long");

	const buildLibrariesUrl = (page) => {
		const params = new URLSearchParams({
			"options[paginate]": "1",
			"options[limit]": String(itemsPerPage),
			"options[page]": String(page),
		});

		if (typeof year !== "undefined" && year !== null) {
			params.append("options[year]", year);
		}

		return `/bseitwp/v1/libraries/filter/pdf?${params.toString()}`;
	};

	const fetchLibraries = useCallback(
		async (page = 1) => {
			setLoading(true);
			setError(null);

			try {
				// console.log(`Fetching libraries for page ${page}`);
				const response = await fetchData(buildLibrariesUrl(page));
				setLibraries(response.data?.data || []);
				setTotalPages(response.data?.pagination?.total_pages || 1);
			} catch (error) {
				setError(error);
				setLibraries([]);
			} finally {
				setLoading(false);
			}
		},
		[itemsPerPage, year],
	);

	useEffect(() => {
		fetchLibraries(currentPage);
	}, [fetchLibraries, currentPage]);

	// Custom styles to fix RTL issues with Fancybox: The PDF viewer works perfectly in the English (LTR) layout but shows a black screen with only navigation arrows in the Arabic (RTL) layout.
	const fancyboxStyles = `
    [dir="rtl"] .fancybox__container,
    [dir="rtl"] .fancybox__slide,
    [dir="rtl"] .fancybox__content,
	[dir="rtl"] .fancybox__carousel,
	[dir="rtl"] .fancybox__track {
        direction: ltr !important;
    }

    [dir="rtl"] .fancybox__content iframe {
        left: 0 !important;
        right: auto !important;
    }
`;

	useEffect(() => {
		// The FlippingBook viewer mode opens publications in a new tab, so
		// FancyBox must stay completely unbound and its styles uninjected.
		if (flippingBookViewerEnabled) {
			return () => {};
		}

		// Inject styles once
		const styleEl = document.createElement("style");
		styleEl.textContent = fancyboxStyles;
		document.head.appendChild(styleEl);

		Fancybox.bind("[data-fancybox]", {
			Toolbar: false,
			Thumbs: false,
		});

		return () => {
			Fancybox.unbind("[data-fancybox]");
			styleEl.remove();
		};
	}, [libraries, currentPage, flippingBookViewerEnabled]);

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	return (
		<>
			<div className="md:px-5 lg:px-0">
				{loading && (
					<div className="flex items-center justify-center w-full">
						<Spinner
							style={{
								height: "calc(2px * 20)",
								width: "calc(2px * 20)",
							}}
						/>
					</div>
				)}

				{error && <p>{error.message}</p>}

				{!loading && libraries && libraries.length > 0 &&
					libraries.map((item, index) => {
						const viewer = getLibraryViewer(
							item,
							flippingBookViewerEnabled,
						);
						const pdfDownloadUrl =
							typeof item.meta_data.pdf === "string" &&
							item.meta_data.pdf !== ""
								? item.meta_data.pdf
								: "";
						const showDownloadButton =
							Boolean(downloadBtn) && pdfDownloadUrl !== "";
						// Historical records without a usable viewer stay
						// readable, but nothing about them acts as a link.
						let viewerLinkAttributes = {};
						if (viewer.isAvailable && viewer.opensInNewTab) {
							viewerLinkAttributes = {
								href: viewer.url,
								target: "_blank",
								rel: "noopener noreferrer",
							};
						} else if (viewer.isAvailable) {
							viewerLinkAttributes = {
								"data-fancybox": `group${item.id}`,
								"data-type": "iframe",
								"data-height": "100%",
								"data-width": "100%",
								href: viewer.url,
							};
						}
						return (
							<div
								key={index}
								className="block transition rounded-lg even:bg-lightGray lg:even:bg-transparent mb-4 lg:mb-0 p-4 lg:p-0 lg:hover:bg-lightGray"
							>
								<div className="grid items-center justify-between mb-5 md:grid-cols-10">
									<div className="lg:col-span-1 md:col-span-1">
										{viewer.isAvailable ? (
											<a
												{...viewerLinkAttributes}
												className="block text-3xl font-bold lg:text-5xl lg:text-center"
											>
												{(currentPage - 1) * itemsPerPage + index + 1 < 10
													? `0${(currentPage - 1) * itemsPerPage + index + 1}`
													: (currentPage - 1) * itemsPerPage + index + 1}
											</a>
										) : (
											<span className="block text-3xl font-bold lg:text-5xl lg:text-center">
												{(currentPage - 1) * itemsPerPage + index + 1 < 10
													? `0${(currentPage - 1) * itemsPerPage + index + 1}`
													: (currentPage - 1) * itemsPerPage + index + 1}
											</span>
										)}
									</div>
									<div className="lg:col-span-2 md:col-span-3">
										{viewer.isAvailable ? (
											<a
												{...viewerLinkAttributes}
												className="block group my-5 transition rounded-lg w-40 h-52 lg:w-[200px] lg:h-[248px] lg:mx-auto lg:block overflow-hidden"
											>
												<img
													className="object-cover w-full h-full transition lg:group-hover:scale-110"
													src={item.thumbnail}
													alt={item.title}
													loading="lazy"
												/>
											</a>
										) : (
											<span className="block group my-5 transition rounded-lg w-40 h-52 lg:w-[200px] lg:h-[248px] lg:mx-auto lg:block overflow-hidden">
												<img
													className="object-cover w-full h-full transition lg:group-hover:scale-110"
													src={item.thumbnail}
													alt={item.title}
													loading="lazy"
												/>
											</span>
										)}
									</div>
									<div className="pb-5 lg:col-span-6 md:col-span-5">
										<div className="lg:px-12">
											<h3 className="text-xl font-bold lg:text-3xl">
												{viewer.isAvailable ? (
													<a {...viewerLinkAttributes} className="block">
														{BSHelper.truncate(item.title, 60)}
													</a>
												) : (
													<span className="block">
														{BSHelper.truncate(item.title, 60)}
													</span>
												)}
											</h3>
											{viewer.isAvailable ? (
												<a
													{...viewerLinkAttributes}
													className="block pt-5 text-darkGray"
												>
													{BSHelper.truncate(
														item.meta_data.short_description,
														140,
													)}
												</a>
											) : (
												<span className="block pt-5 text-darkGray">
													{BSHelper.truncate(
														item.meta_data.short_description,
														140,
													)}
												</span>
											)}
										</div>
									</div>
									<div className="lg:text-center lg:col-span-1 md:col-span-1">
										{showDownloadButton && (
											<a
												className="hidden lg:inline-flex items-center justify-center w-12 h-12 p-2 text-white transition-all rounded-full bg-primary group mx-1 my-1"
												href={pdfDownloadUrl}
												target="_blank"
												rel="noopener noreferrer"
												download={true}
											>
												<i
													className={`fa-solid fa-download `}
												></i>
											</a>
										)}
										{viewer.isAvailable && (
											<a
												{...viewerLinkAttributes}
												className="hidden lg:inline-flex items-center justify-center w-12 h-12 p-2 text-white transition-all rounded-full ltr:-rotate-45 bg-primary rtl:rotate-45 hover:rotate-0 group"
											>
												<i
													className={`fa-solid ${arrowClassIcon} group-hover:rotate-0`}
												></i>
											</a>
										)}
										{showDownloadButton && (
											<a
												className="lg:hidden px-3 py-1.5 lg:inline-flex items-center justify-center w-12 h-12 text-white transition-all rounded-full bg-primary group mx-1 my-1"
												href={pdfDownloadUrl}
												target="_blank"
												rel="noopener noreferrer"
												download={true}
											>
												<i
													className={`fa-solid fa-download `}
												></i>
											</a>
										)}
									</div>
								</div>
							</div>
						);
					})}

				{!loading && totalPages > 1 && (
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
					/>
				)}
			</div>
		</>
	);
}
